import { type DisplayLanguage, ImageSelectorTab } from '@amzn/journeys-common';
import { Button, Modal, ModalFooter, Row, SideMenu, SideMenuLink } from '@amzn/meridian';
import documentIcons from '@amzn/meridian-tokens/base/icon/document';
import folderTokens from '@amzn/meridian-tokens/base/icon/folder';
import linkTokens from '@amzn/meridian-tokens/base/icon/link';
import plusTokens from '@amzn/meridian-tokens/base/icon/plus';
import uploadLargeTokens from '@amzn/meridian-tokens/base/icon/upload-large';
import { includes, isEmpty, map } from 'es-toolkit/compat';
import { produce } from 'immer';
import { type FC, useCallback, useMemo, useReducer, useRef } from 'react';
import { stylesheet } from 'typestyle';
import { assertUnreachable } from '../../app.util';
import type { SummaryGroup } from '../filters/filters.util';
import { AssetCreatorContainer } from './AssetCreatorContainer';
import { IMAGE_SELECTOR_MODAL_DATA_ATTRIBUTE } from './assets.constants';
import { type AssetSelection, defaultLanguageSelection, getImageFileFromUrl } from './assets.util';
import { ImageFromMediaCentralSelector } from './ImageFromMediaCentralSelector';
import { ImagesFromAllProjectsSelector } from './ImagesFromAllProjectsSelector';
import { ImagesFromFileSystemSelector } from './ImagesFromFileSystemSelector';
import { ImagesFromProjectSelector } from './ImagesFromProjectSelector';
import type { ImageSelectorModalAction } from './image-selector-modal.actions';
import type { ImageSelectorModalState, ImageTags } from './image-selector-modal.types';

export type ImageSelectorModalProps = {
  onHide: () => void;
  onInserted: (selections: AssetSelection[], tags: ImageTags) => void;
  multiselect: boolean;
  projectId: string;
  language?: DisplayLanguage;
  // Params that will be used to create filters inside of the image-selector-modal
  // starting off this will just be dimensions.
  filterParams: SummaryGroup[];
  validFileTypes?: string[];
  visible: boolean;
  pageSize?: number;
  visibleTabs?: ImageSelectorTab[];
  projectName?: string;
};

export const css = stylesheet({
  dropZoneTabContainer: {
    height: '100%',
  },
  modalBody: {
    display: 'flex',
    overflowX: 'hidden',
    padding: 0,
    width: 'auto',
  },
  previewImage: {
    maxHeight: '100%',
    maxWidth: '100%',
  },
  tabContent: {
    flexGrow: 1,
    height: 'calc(100vh - 200px)',
    maxHeight: '600px',
    minHeight: '200px',
    overflowY: 'auto',
    padding: '10px 15px 0 15px',
    width: 'calc(100vh - 20px)',
  },
});

function getInitState(): ImageSelectorModalState {
  return {
    activeTab: ImageSelectorTab.thisProject,
    fromAllProjects: {
      selectedAssets: [],
    },
    fromFileSystem: {
      files: [],
      hasInvalidFiles: false,
      makeVisibleForEveryone: false,
      tags: {
        device: [],
        platform: [],
        productLine: [],
        tags: [],
      },
    },
    fromMediaCentral: {
      mediaCentralUrl: '',
    },
    fromThisProject: {
      selectedAssets: [],
    },
  };
}

function reducer(state: ImageSelectorModalState, action: ImageSelectorModalAction): ImageSelectorModalState {
  switch (action.type) {
    case 'selectedAssetsChanged':
      return produce(state, (draft) => {
        if (action.from === ImageSelectorTab.thisProject) {
          draft.fromThisProject.selectedAssets = action.selectedAssets;
        } else {
          draft.fromAllProjects.selectedAssets = action.selectedAssets;
        }
      });
    case 'filesSelectedChanged':
      return produce(state, (draft) => {
        draft.fromFileSystem.files = action.files;
        draft.fromFileSystem.hasInvalidFiles = action.hasInvalidFiles;
      });
    case 'tagsChanged':
      return produce(state, (draft) => {
        draft.fromFileSystem.tags[action.tagType] = action.tags;
      });
    case 'makeVisibleForEveryoneChanged':
      return produce(state, (draft) => {
        draft.fromFileSystem.makeVisibleForEveryone = action.makeVisibleForEveryone;
      });
    case 'mediaCentralUrlChanged':
      return produce(state, (draft) => {
        draft.fromMediaCentral.mediaCentralUrl = action.mediaCentralUrl;
      });
    case 'resetState':
      return getInitState();
    case 'setTab':
      return produce(state, (draft) => {
        draft.activeTab = action.activeTab;
      });
    default:
      assertUnreachable(action);
  }
}

async function getAssetSelectionsFromState({
  mediaCentralUrl,
  state,
}: {
  mediaCentralUrl: string;
  state: ImageSelectorModalState;
}): Promise<AssetSelection[] | Error> {
  switch (state.activeTab) {
    case ImageSelectorTab.thisProject:
      return state.fromThisProject.selectedAssets;
    case ImageSelectorTab.allProjects:
      return state.fromAllProjects.selectedAssets;
    case ImageSelectorTab.mediaCentral:
      try {
        const file = await getImageFileFromUrl(mediaCentralUrl);
        return [
          {
            assetFile: file,
          },
        ];
      } catch {
        // in rare cases such as the server serving the image URL not allowing CORS requests, the image
        // will fail to load.
        return {
          message:
            'Error fetching image from URL. Please try downloading the image ' +
            'manually to your filesystem and then uploading the saved image file to Journeys.',
          name: 'MC fetching error',
        };
      }
    case ImageSelectorTab.fileSystem:
      return map(state.fromFileSystem.files, (file) => ({
        assetFile: file,
      }));
  }

  return {
    message: 'Could not get selections for tab',
    name: 'invalid tab',
  };
}

export const ImageSelectorModal: FC<ImageSelectorModalProps> = ({
  visible,
  multiselect,
  language = defaultLanguageSelection,
  filterParams,
  projectName,
  projectId,
  onHide,
  pageSize = 50,
  onInserted,
  validFileTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
  ],
  visibleTabs = [
    ImageSelectorTab.thisProject,
    ImageSelectorTab.fileSystem,
    ImageSelectorTab.allProjects,
    ImageSelectorTab.mediaCentral,
  ],
}) => {
  const [state, dispatch] = useReducer(reducer, getInitState());
  const searchInputRef = useRef<HTMLInputElement>(null);
  const onShow = () => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const insertBtnClicked = async () => {
    const selections = await getAssetSelectionsFromState({
      mediaCentralUrl: state.fromMediaCentral.mediaCentralUrl,
      state,
    });

    if (Array.isArray(selections)) {
      onInserted(selections, state.fromFileSystem.tags);
    }
  };

  const tabIsVisible = (tab: ImageSelectorTab) => includes(visibleTabs, tab);
  const maxFilesAllowed = 50;

  const numSelected = useMemo((): number => {
    switch (state.activeTab) {
      case ImageSelectorTab.thisProject:
        return state.fromThisProject.selectedAssets.filter(Boolean).length;
      case ImageSelectorTab.allProjects:
        return state.fromAllProjects.selectedAssets.filter(Boolean).length;
      case ImageSelectorTab.fileSystem:
        return state.fromFileSystem.files?.length ?? 0;
    }
    return 0;
  }, [
    state.activeTab,
    state.fromAllProjects.selectedAssets,
    state.fromFileSystem.files,
    state.fromThisProject.selectedAssets,
  ]);

  const insertBtnDisabled = useMemo(() => {
    const mediaCentralUrl = state.fromMediaCentral.mediaCentralUrl;
    switch (state.activeTab) {
      case ImageSelectorTab.thisProject:
      case ImageSelectorTab.allProjects:
      case ImageSelectorTab.fileSystem:
        return numSelected === 0 || state.fromFileSystem.hasInvalidFiles;
      case ImageSelectorTab.mediaCentral:
        return (
          isEmpty(mediaCentralUrl) ||
          !(
            mediaCentralUrl.endsWith('.jpg') ||
            mediaCentralUrl.endsWith('.jpeg') ||
            mediaCentralUrl.endsWith('.png') ||
            mediaCentralUrl.endsWith('.gif') ||
            mediaCentralUrl.endsWith('.mp4') ||
            mediaCentralUrl.endsWith('.webm')
          )
        );
    }
  }, [
    numSelected,
    state.activeTab,
    state.fromFileSystem.hasInvalidFiles,
    state.fromMediaCentral.mediaCentralUrl,
  ]);

  const modalTitle = 'Insert media';
  const imgLabel = numSelected > 1 ? 'images' : 'image';
  const insertLabel = multiselect && numSelected > 0 ? `Insert ${numSelected} ${imgLabel}` : 'Insert';

  const setTab = (eventKey: ImageSelectorTab) => {
    dispatch({
      activeTab: eventKey,
      type: 'setTab',
    });
  };

  const onExited = () => {
    dispatch({
      type: 'resetState',
    });
  };

  const isTabSelected = useCallback(
    (tabKey: ImageSelectorTab) => state.activeTab === tabKey,
    [
      state.activeTab,
    ],
  );

  return (
    <Modal
      // id={IMAGE_SELECTOR_MODAL_ROOT_ID}
      bodySpacingInset="none"
      data-cy="image-selector"
      onClose={() => {
        onHide();
        onExited();
      }}
      onOpen={onShow}
      open={visible}
      scrollContainer="modal"
      title={modalTitle}
      {...{
        [IMAGE_SELECTOR_MODAL_DATA_ATTRIBUTE]: true,
        foo: 'bar',
      }}
    >
      <div className={css.modalBody}>
        <SideMenu>
          {tabIsVisible(ImageSelectorTab.thisProject) && (
            <SideMenuLink
              data-cy={`asset-modal-nav-link-${ImageSelectorTab.thisProject}`}
              icon={documentIcons}
              onClick={() => setTab(ImageSelectorTab.thisProject)}
              selected={isTabSelected(ImageSelectorTab.thisProject)}
            >
              From this project
            </SideMenuLink>
          )}
          {tabIsVisible(ImageSelectorTab.fileSystem) && (
            <SideMenuLink
              data-cy={`asset-modal-nav-link-${ImageSelectorTab.fileSystem}`}
              icon={uploadLargeTokens}
              onClick={() => setTab(ImageSelectorTab.fileSystem)}
              selected={isTabSelected(ImageSelectorTab.fileSystem)}
            >
              From your file system
            </SideMenuLink>
          )}
          {tabIsVisible(ImageSelectorTab.allProjects) && (
            <SideMenuLink
              data-cy={`asset-modal-nav-link-${ImageSelectorTab.allProjects}`}
              icon={folderTokens}
              onClick={() => setTab(ImageSelectorTab.allProjects)}
              selected={isTabSelected(ImageSelectorTab.allProjects)}
            >
              From all projects
            </SideMenuLink>
          )}
          {tabIsVisible(ImageSelectorTab.mediaCentral) && (
            <SideMenuLink
              data-cy={`asset-modal-nav-link-${ImageSelectorTab.mediaCentral}`}
              icon={linkTokens}
              onClick={() => setTab(ImageSelectorTab.mediaCentral)}
              selected={isTabSelected(ImageSelectorTab.mediaCentral)}
            >
              From Media Central
            </SideMenuLink>
          )}
          {tabIsVisible(ImageSelectorTab.createAnAsset) && (
            <SideMenuLink
              data-cy={`asset-modal-nav-link-${ImageSelectorTab.createAnAsset}`}
              icon={plusTokens}
              onClick={() => setTab(ImageSelectorTab.createAnAsset)}
              selected={isTabSelected(ImageSelectorTab.createAnAsset)}
            >
              Create an asset
            </SideMenuLink>
          )}
        </SideMenu>
        <div className={css.tabContent}>
          {isTabSelected(ImageSelectorTab.thisProject) && (
            <ImagesFromProjectSelector
              dispatch={dispatch}
              filterParams={filterParams}
              multiselect={multiselect}
              pageSize={pageSize}
              projectId={projectId}
              selectedAssets={state.fromThisProject.selectedAssets}
              visible={visible && state.activeTab === ImageSelectorTab.thisProject}
            />
          )}
          {isTabSelected(ImageSelectorTab.fileSystem) && (
            <div className={css.dropZoneTabContainer}>
              <ImagesFromFileSystemSelector
                dispatch={dispatch}
                files={state.fromFileSystem.files}
                makeVisibleForEveryone={state.fromFileSystem.makeVisibleForEveryone}
                maxFilesAllowed={maxFilesAllowed}
                multiselect={multiselect}
                tags={state.fromFileSystem.tags}
                validFileTypes={validFileTypes}
              />
            </div>
          )}
          {isTabSelected(ImageSelectorTab.allProjects) && (
            <ImagesFromAllProjectsSelector
              dispatch={dispatch}
              multiselect={multiselect}
              pageSize={pageSize}
              selectedAssets={state.fromAllProjects.selectedAssets}
              visible={visible && state.activeTab === ImageSelectorTab.allProjects}
            />
          )}
          {isTabSelected(ImageSelectorTab.mediaCentral) && <ImageFromMediaCentralSelector defaultLanguage={language} dispatch={dispatch} />}
          {isTabSelected(ImageSelectorTab.createAnAsset) && (
            <AssetCreatorContainer
              onLaunched={() => {
                onHide();
              }}
              projectId={projectId}
              projectName={projectName ?? ''}
            />
          )}
        </div>
      </div>

      <ModalFooter>
        <Row alignmentHorizontal="end">
          <Button data-cy="cancel-btn" onClick={onHide} type="secondary">
            Cancel
          </Button>
          {state.activeTab !== ImageSelectorTab.createAnAsset && (
            <Button data-cy="insert-btn" disabled={insertBtnDisabled} onClick={insertBtnClicked} type="primary">
              {insertLabel}
            </Button>
          )}
        </Row>
      </ModalFooter>
    </Modal>
  );
};
